import React, { useMemo } from "react";
import { List, Users, Building2, HardHat, Grid2X2, Zap } from "lucide-react";
import { Card } from "@shared/ui/card";
import { ListOverview, ListsOverviewProps } from "../../types";
import { isListOverview } from "../../utils";
import { SectionWrapper } from "../shared/sectionWrapper";
import { MetricCard } from "../shared/metricCard";
import { ProgressItem } from "../shared/progressItem";

const METRICS_CONFIG = [
  {
    key: "totalLists",
    label: "Total Lists",
    icon: List,
    changeKey: "totalListsChange",
    fallback: "NA",
  },
  {
    key: "totalContacts",
    label: "Total Contacts",
    icon: Users,
    changeKey: "totalContactsChange",
    fallback: "NA",
  },
] as const;

const CATEGORY_CONFIG = [
  {
    key: "consumer" as const,
    label: "Consumers",
    icon: Users,
    color: "text-blue-500",
  },
  {
    key: "technician" as const, // Changed from "technicians"
    label: "Technicians",
    icon: HardHat,
    color: "text-amber-500",
  },
  {
    key: "businessUser" as const,
    label: "Business",
    icon: Building2,
    color: "text-green-500",
  },
] as const;

export const ListsOverview: React.FC<ListsOverviewProps> = ({ data }) => {
  const metricsData = useMemo(() => {
    if (!data || !isListOverview(data)) return [];

    return METRICS_CONFIG.map((config) => ({
      ...config,
      value: data[config.key] ?? config.fallback ?? 0,
      change: data[config.changeKey],
    }));
  }, [data]);

  const categoriesData = useMemo(() => {
    if (!data || !isListOverview(data)) return [];

    const percentageKeyMap = {
      consumer: "consumer",
      technician: "technician",
      businessUser: "business_user",
    };

    return CATEGORY_CONFIG.map((config) => ({
      ...config,
      count: data[config.key], // Uses the actual count values
      percentage: data.recipientTypeDistribution[percentageKeyMap[config.key]],
    }));
  }, [data]);

  return (
    <SectionWrapper
      title="Lists Overview"
      isEmpty={!data || !isListOverview(data)}
      loadingType="stats"
      loadingCount={3}
    >
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {metricsData.map((metric) => (
          <MetricCard
            position="side"
            key={metric.key}
            title={metric.label}
            value={metric.value}
            icon={metric.icon}
            change={metric.change}
            variant="default"
          />
        ))}

        {/* Static/Dynamic Lists Card */}
        {isListOverview(data) && (
          <Card className=" p-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Grid2X2 className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium">Static Lists</span>
                </div>
                <span className="text-xl font-semibold">
                  {data.staticListsCount || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium">Dynamic Lists</span>
                </div>
                <span className="text-xl font-semibold">
                  {data.dynamicListsCount || 0}
                </span>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* List Categories */}
      <Card className="p-6">
        <h4 className="card-heading-text mb-4">List Categories</h4>
        <div className="space-y-6">
          {categoriesData.map((category) => (
            <ProgressItem
              key={category.key}
              label={category.label}
              value={category.count}
              percentage={category.percentage}
              icon={category.icon}
              iconColor={category.color}
              subtitle={`${category.count} lists`}
              showValue={false}
              showPercentage={true}
            />
          ))}
        </div>
      </Card>
    </SectionWrapper>
  );
};
